import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addPitch, getPitches, editPitch, deletePitch } from '../../services/api';

// Async thunks
export const fetchPitches = createAsyncThunk(
  'pitch/fetchPitches',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPitches();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createPitch = createAsyncThunk(
  'pitch/createPitch',
  async (pitchData, { rejectWithValue, getState }) => {
    try {
      // Get user ID from the state
      const { user } = getState().user;
      
      // Prepare the complete payload
      const payload = {
        name: pitchData.name,
        user: user._id, // Get user ID from the user state
        sport: pitchData.sport,
        facility: pitchData.facility,
        maxPlayers: pitchData.maxPlayers
      };

      const response = await addPitch(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updatePitch = createAsyncThunk(
  'pitch/updatePitch',
  async (pitchData, { rejectWithValue }) => {
    try {
      const response = await editPitch(pitchData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removePitch = createAsyncThunk(
  'pitch/removePitch',
  async (pitchId, { rejectWithValue }) => {
    try {
      const response = await deletePitch(pitchId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  pitches: [],
  loading: false,
  error: null,
  success: false,
  message: null
};

const pitchSlice = createSlice({
  name: 'pitch',
  initialState,
  reducers: {
    clearPitchState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch pitches
      .addCase(fetchPitches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPitches.fulfilled, (state, action) => {
        state.loading = false;
        state.pitches = action.payload.pitch;
      })
      .addCase(fetchPitches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create pitch
      .addCase(createPitch.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createPitch.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(createPitch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update pitch
      .addCase(updatePitch.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updatePitch.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(updatePitch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete pitch
      .addCase(removePitch.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(removePitch.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(removePitch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearPitchState } = pitchSlice.actions;
export default pitchSlice.reducer; 